import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getHeatmap,
  ValidationError,
  NotFoundError,
} from '../controllers/leetcodeAnalyticsController';

/**
 * Fetches a GitHub-style LeetCode submission activity heatmap for one student.
 *
 * Re-fetches automatically whenever userId, startDate, or endDate changes.
 * Cancels in-flight requests when dependencies change mid-flight so stale
 * responses never overwrite newer state.
 *
 * @param {string|null|undefined} userId    - User._id or StudentProfile._id
 * @param {string|null|undefined} startDate - 'YYYY-MM-DD'
 * @param {string|null|undefined} endDate   - 'YYYY-MM-DD'
 *
 * @returns {{
 *   heatmapData: Array<{ date: string, count: number }> | null,
 *   loading:     boolean,
 *   error:       string | null,
 *   errorType:   'validation' | 'notFound' | 'server' | null,
 *   refetch:     () => void
 * }}
 *
 * @example
 * const { heatmapData, loading, error } = useLeetcodeHeatmap(
 *   userId,
 *   '2026-01-01',
 *   '2026-03-14'
 * );
 */
const useLeetcodeHeatmap = (userId, startDate, endDate) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [errorType, setErrorType]     = useState(null);

  // Used to ignore responses from superseded requests
  const cancelledRef = useRef(false);

  // Manual refetch counter — incrementing triggers a re-fetch even when
  // userId / dates haven't changed (e.g. after a transient network error).
  const [fetchCount, setFetchCount] = useState(0);
  const refetch = useCallback(() => setFetchCount((n) => n + 1), []);

  useEffect(() => {
    // Skip if required params are absent
    if (!userId || !startDate || !endDate) {
      setHeatmapData(null);
      setError(null);
      setErrorType(null);
      setLoading(false);
      return;
    }

    // Mark any concurrent stale effect as cancelled
    cancelledRef.current = false;
    const cancelled = () => cancelledRef.current;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setErrorType(null);

      try {
        const result = await getHeatmap(userId, startDate, endDate);

        if (cancelled()) return; // discard — a newer request is in flight

        setHeatmapData(result.heatmap);
      } catch (err) {
        if (cancelled()) return;

        if (err instanceof ValidationError) {
          setErrorType('validation');
        } else if (err instanceof NotFoundError) {
          setErrorType('notFound');
        } else {
          setErrorType('server');
        }

        setError(err.message ?? 'An unexpected error occurred.');
        setHeatmapData(null);
      } finally {
        if (!cancelled()) setLoading(false);
      }
    };

    fetchData();

    // Cancellation cleanup: flag the running effect as stale
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, startDate, endDate, fetchCount]);

  return { heatmapData, loading, error, errorType, refetch };
};

export default useLeetcodeHeatmap;
